import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { relativeTime } from '@/lib/format'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { STAGE_LABELS, STAGE_TONES, formatScore } from './stages'

type Column = Schema<'BoardColumn'>
type Card = Schema<'BoardCard'>

/** Indian digit grouping, as `Leads.tsx` uses on the same figure — the board
 *  and the list must not print one tenant's lead count two ways, and the mockup
 *  draws it grouped (`outreach.html:661`). */
const GROUPED = new Intl.NumberFormat('en-IN')

/**
 * The CRM board — `design/screens/outreach.html#crm`, audit row §B3.
 *
 * Four columns because §5's pipeline has four states, and the prototype's own
 * note says why it is not the six-stage dashboard funnel: *"posts and opens are
 * things that happen to a lead, not places a lead sits."*
 *
 * WHY THERE IS NO MONEY ON THIS BOARD
 * -------------------------------------
 * The prototype's header read *"₹8.4L in play"* and its won cards carried
 * *"₹2.4L"*. There is no deal value anywhere in AugRank — §5's pipeline is four
 * states and says nothing about amounts — so every one of those figures would be
 * a number the product invented. The heading carries the count instead, which is
 * true. OUTR-81 closed that the other way round as well: the mockup no longer
 * draws the money, so no later session has to rediscover the argument.
 *
 * WHAT A CARD SAYS, AND WHY IT DEPENDS ON THE COLUMN
 * ----------------------------------------------------
 * OUTR-82. The design writes *"sent 3 days ago"*, *"opened 4×, no reply"*,
 * *"asked for a rate card"* — a different fact per column, because the useful
 * fact about a lead **is** different per column. The score is what distinguishes
 * one *found* lead from another and says nothing at all about a *won* one, where
 * the question is when. So the second line follows the stage: the score while
 * nothing has happened, and the last thing that did once something has.
 *
 * Opens are not among them and the count of them is not invented: `messages`
 * records delivery states and not read receipts per recipient, so *"opened 4×"*
 * has no producer. The two warnings the board can prove — a rung that is late
 * and a reply nobody has read — are the badges (OUTR-83).
 *
 * WHY THE CARDS DO NOT DRAG
 * ---------------------------
 * Moving a lead is `POST /outreach/leads/{id}/stage` and it exists; what is not
 * here is drag-and-drop as the way to reach it. A board with four columns and
 * six cards each is a *glance* — the count in the heading is the real figure and
 * the cards are examples — and a drag target on a partial column is an invitation
 * to reorganise a pipeline through a window showing a tenth of it. The move lives
 * on the lead, where the whole of that lead is on screen, and in the inbox, where
 * the conversation that justifies it is.
 */
export function Pipeline(): JSX.Element {
  const board = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/pipeline', { signal })),
    [],
  )

  const columns = board.state.status === 'ready' ? board.state.data.columns : []
  const total = columns.reduce((sum, column) => sum + column.total, 0)

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row app-board__top">
        <h1 className="ar-h1">Pipeline</h1>
        {/* OUTR-119. The design's right-hand slot is a badge, and the badge said
            *"₹8.4L in play"*. The shape is kept and the figure is one the
            product can prove: how many leads are on the board at all. */}
        {board.state.status === 'ready' ? (
          <span className="ar-badge ar-badge--neutral">
            {total === 1 ? '1 lead in play' : `${GROUPED.format(total)} leads in play`}
          </span>
        ) : null}
      </div>

      {board.state.status === 'loading' ? <Loading label="Loading the pipeline" lines={5} /> : null}
      {board.state.status === 'error' ? (
        <ErrorState
          failure={board.state.failure}
          onRetry={board.refetch}
          title="Could not load the pipeline"
        />
      ) : null}
      {board.state.status === 'ready' ? <Board columns={columns} /> : null}
    </div>
  )
}

function Board({ columns }: { columns: Column[] }): JSX.Element {
  const empty = columns.every((column) => column.total === 0)
  if (empty)
    return (
      <div className="ar-card">
        <div className="ar-card__body">
          <Empty
            // `⌕`, the default and the glyph every other outreach empty state
            // uses; `○` rendered as a dot inside the circle, the placeholder
            // `states.tsx` retired (walkthrough 2026-09-12).
            title="Nothing in the pipeline yet"
            body="Leads appear here as your browser extension finds them, and move across as they are contacted and reply. The four columns are the whole of the pipeline: found, contacted, replied, won."
          />
        </div>
      </div>
    )

  // One clock for the whole board, so two cards a millisecond apart cannot
  // render "3 days ago" and "4 days ago" for the same moment.
  const now = new Date()

  return (
    <div className="app-board">
      {columns.map((column) => (
        <section className="app-board__col" key={column.stage}>
          <header className="app-board__head">
            <span>{STAGE_LABELS[column.stage] ?? column.stage}</span>
            <span className={`ar-badge ar-badge--${STAGE_TONES[column.stage] ?? 'neutral'}`}>
              {column.total}
            </span>
          </header>
          {/* No line under the heading (parity P-CRM-02): `outreach.html#crm`
              draws the heading, the count, and the cards. */}
          {column.cards.length === 0 ? (
            <p className="ar-subtle app-board__empty">Nothing here.</p>
          ) : (
            column.cards.map((card) => <BoardCard key={card.lead.id} card={card} now={now} />)
          )}
          {column.total > column.cards.length ? (
            <Link className="ar-link app-board__more" to={`/outreach/leads?stage=${column.stage}`}>
              {column.total - column.cards.length} more
            </Link>
          ) : null}
        </section>
      ))}
    </div>
  )
}

function BoardCard({ card, now }: { card: Card; now: Date }): JSX.Element {
  const { lead } = card
  return (
    <Link className="app-board__card" to={`/outreach/leads/${lead.id}`}>
      <strong>{lead.full_name ?? 'Unnamed lead'}</strong>
      {/* `company` holds the headline when the capture could not split one
          from the other (P-CRM-03); a company equal to the headline is left
          out rather than printed as a seven-line second line. */}
      <span className="ar-subtle">
        {[lead.company === lead.headline ? null : lead.company, context(card, now)]
          .filter(Boolean)
          .join(' · ')}
      </span>
      {card.needs_reply ? (
        <span className="ar-badge ar-badge--warning app-board__flag">Needs a reply</span>
      ) : null}
      {/* Only one warning per card, and *needs a reply* is the one that wins:
          a prospect who has written to you outranks a follow-up that is late,
          and two badges on a card the size of a business card is neither. */}
      {!card.needs_reply && card.follow_up_due ? (
        <span className="ar-badge ar-badge--neutral app-board__flag">Follow-up due</span>
      ) : null}
    </Link>
  )
}

/**
 * The card's second fact, chosen by the stage it sits in — OUTR-82.
 *
 * Every stamp read here is one the pipeline itself writes (`app/v1/leads.py`
 * `set_stage`, and the send path for `contacted_at`), so the line is never a
 * guess: where the stamp is missing — a lead moved by hand into `won` before
 * anything was ever sent — it falls back to the score rather than inventing a
 * date, and a lead nobody scored falls back to nothing at all.
 */
function context(card: Card, now: Date): string {
  const { lead } = card
  const at =
    lead.stage === 'won'
      ? (lead.won_at ?? null)
      : lead.stage === 'replied'
        ? (lead.replied_at ?? null)
        : lead.stage === 'contacted'
          ? (lead.contacted_at ?? null)
          : null
  if (at !== null) {
    const verb = lead.stage === 'contacted' ? 'sent' : lead.stage === 'replied' ? 'replied' : 'won'
    return `${verb} ${relativeTime(at, now).toLowerCase()}`
  }
  const score = formatScore(lead.relevance_score ?? null)
  return score === '—' ? '' : `score ${score}`
}
